import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grow,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Form, FormikProps, FormikProvider } from 'formik';
import Logo from 'src/components/Logo';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import axios from 'src/utils/axios';
import { useNavigate } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import ContinueWithProviderButton from 'src/components/authentication/federated/ContinueWithProviderButton';
import { FederatedProvider } from 'src/components/authentication/federated/federatedAuth';
import { detectEmailProvider } from 'src/components/authentication/federated/providerDetection';
import { providerLabel } from 'src/components/authentication/federated/providerBranding';
import { useFederatedProviders } from 'src/components/authentication/federated/useFederatedProviders';

const FALLBACK_PREFERENCE_KEY = 'federated:prefer-password';

interface FormValues {
  username: string;
  password: string;
}

export interface LoginFormV2Props {
  formik: FormikProps<FormValues>;
  formStep: LOGIN_FORM_STEP;
  setLoginFormStep: React.Dispatch<React.SetStateAction<LOGIN_FORM_STEP>>;
  extendSession?: boolean;
  loginError?: string;
  /** Compact mode: hide logo and "Login" heading (embedded in self-registration, etc.) */
  compact?: boolean;
  /** Called when send_sms_code returns 429 — advances to VERIFY_CODE with pre-seeded cooldown */
  onPhoneLoginCooldown?: (cooldownSeconds: number) => void;
}

enum INPUT_TYPES {
  EMAIL = 'email',
  PHONE = 'phone',
}

export const phoneRegex =
  /^(\([0-9]{3}\)|[0-9]{3})([\s.-]?)[0-9]{3}([\s.-]?)[0-9]{4}$/;
export const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

export enum LOGIN_FORM_STEP {
  'LOGIN' = 'LOGIN',
  'RESET_PASSWORD' = 'RESET_PASSWORD',
  'VERIFY_CODE' = 'VERIFY_CODE',
  'LOGIN_LINK_SENT' = 'LOGIN_LINK_SENT',
}

interface userLoginOption {
  value: string;
}

const LoginFormV2: FC<LoginFormV2Props> = ({
  formik,
  formStep,
  setLoginFormStep,
  extendSession,
  loginError,
  compact = false,
  onPhoneLoginCooldown,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const enabledProviders = useFederatedProviders();
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)'
  );
  // ENG-3554: remember a renter's "use a password" choice for the session so
  // they are not re-promoted to the provider CTA on every render.
  const [preferPasswordFallback, setPreferPasswordFallback] = useState<boolean>(
    () => {
      try {
        return sessionStorage.getItem(FALLBACK_PREFERENCE_KEY) === '1';
      } catch {
        return false;
      }
    }
  );
  const userLoginOptions = useMemo((): userLoginOption[] => {
    if (!user || !extendSession) {
      return [];
    }

    const options: userLoginOption[] = [
      {
        value: user.email as string,
      },
    ];
    (user.phones || []).forEach((phone: { phone_number: string }) => {
      options.push({
        value: formatPhoneNumber(phone?.phone_number?.replace('+1', '')),
      });
    });

    return options;
  }, [extendSession, user]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    isSubmitting,
  } = formik;

  const { username, password } = values;

  const [isEmail, setIsEmail] = useState(emailRegex.test(username || ''));
  const [isPhone, setIsPhone] = useState(phoneRegex.test(username || ''));
  const [isAutofilled, setIsAutofilled] = useState(false);
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const [inputType, setInputType] = useState<INPUT_TYPES>(
    (isNaN(Number(username || null)) || !username) &&
      !phoneRegex.test(username || null)
      ? INPUT_TYPES.EMAIL
      : INPUT_TYPES.PHONE
  );

  const [showPassword, setShowPassword] = useState(false);
  const [requestErrors, setRequestErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Identifier-first federated sign-in: a Pronto staff email
  // (@prontohousing.com) is sent to the enforced Workspace provider and the
  // password path is hidden for that domain; everyone else is offered Google.
  // Plus-addressed staff-domain addresses (name+tag@prontohousing.com) are
  // test accounts, exempt from enforcement so they can use the password route
  // (ENG-3386).
  const trimmedUsername = (username || '').trim();
  const isStaffDomain =
    /@prontohousing\.com$/i.test(trimmedUsername) &&
    !trimmedUsername.split('@')[0].includes('+');

  // ENG-3395: in the extend-session modal the *authenticated account* — not the
  // identifier the user happens to pick — decides which re-auth routes exist.
  // Staff accounts are enforced-SSO (no usable password; Thor suppresses their
  // password reset), so this modal must offer the IdP. Without it the modal
  // rendered no password field, no submit and no Google button — a dead end.
  const sessionEmail = (user?.email || '').trim();
  const isStaffSessionEmail =
    /@prontohousing\.com$/i.test(sessionEmail) &&
    !sessionEmail.split('@')[0].includes('+');
  const isFederatedAccount = extendSession && isStaffSessionEmail;

  // On the login page the typed identifier governs; in the extend-session modal
  // the authenticated account governs.
  const staffAccount = extendSession ? isFederatedAccount : isStaffDomain;
  const federatedEmail = isFederatedAccount
    ? sessionEmail
    : isEmail
    ? trimmedUsername
    : undefined;

  // ENG-3554: detect the consumer provider from the typed email and, when the
  // backend enables it, make that provider the single primary CTA. The
  // extend-session modal is excluded (ENG-3395: the authenticated account
  // governs, not the typed identifier).
  const detectedProvider: FederatedProvider | null =
    !staffAccount && isEmail ? detectEmailProvider(trimmedUsername) : null;
  const detectedProviderEnabled =
    detectedProvider !== null && enabledProviders.includes(detectedProvider);
  const promotedProvider: FederatedProvider | null =
    !extendSession && detectedProviderEnabled && !preferPasswordFallback
      ? detectedProvider
      : null;

  const showPasswordLogin =
    isEmail && !staffAccount && promotedProvider === null;
  // The password / send-code submit is never offered for an SSO-only account.
  const showCredentialSubmit = !staffAccount && promotedProvider === null;
  // Preserve the login page's "or" divider; in the modal it separates the
  // password path from the IdP only when the password path is actually shown.
  const showGoogleDivider = staffAccount
    ? false
    : extendSession
    ? showPasswordLogin
    : promotedProvider === null;

  // The non-promoted layout offers every backend-enabled consumer provider
  // (staff always get the Workspace tenant).
  const baseProviders: FederatedProvider[] = staffAccount
    ? ['google_workspace']
    : enabledProviders;

  const usePasswordFallback = useCallback((): void => {
    setPreferPasswordFallback(true);
    try {
      sessionStorage.setItem(FALLBACK_PREFERENCE_KEY, '1');
    } catch {
      // sessionStorage unavailable (private mode); the in-memory state applies.
    }
  }, []);

  const autocompleteValue = useMemo(
    () => userLoginOptions.find((item) => item.value === username) || null,
    [userLoginOptions, username]
  );

  const handleUserFieldChange = useCallback(
    (inputValue: string): void => {
      setIsAutofilled(false);
      let value = inputValue;
      const isEmail = emailRegex.test(value);
      const isPhone = phoneRegex.test(value);

      if ((isNaN(Number(value)) && !isPhone) || value === '') {
        setInputType(INPUT_TYPES.EMAIL);
      } else {
        value = formatPhoneNumber(value);
        setInputType(INPUT_TYPES.PHONE);
      }

      void setFieldValue('username', value);
      setIsEmail(isEmail);
      setIsPhone(isPhone);
    },
    [setFieldValue]
  );

  const toggleShowPassword = useCallback((): void => {
    setShowPassword((prevState) => !prevState);
  }, []);

  const validatePhone = useCallback(async (): Promise<void> => {
    setRequestErrors(null);
    setIsLoading(true);
    try {
      const phoneWithUSCodeLength = 12;
      const usPrefix = '+1';

      let phone = username.replace(/[^\d+]/g, '');
      if (
        !(phone.length === phoneWithUSCodeLength || phone.startsWith(usPrefix))
      ) {
        phone = `+1${phone}`;
      }
      // Use new endpoint that works with User.phone_number
      const response = await axios.post('user_phone_login/send_sms_code/', {
        phone,
      });
      if (
        response.data === 'SMS sent' ||
        response.data?.resend_count !== undefined ||
        response.status === 200
      ) {
        setLoginFormStep(LOGIN_FORM_STEP.VERIFY_CODE);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      const status = (
        error as { status?: number; cooldown_seconds_remaining?: number }
      )?.status;
      const cooldown = (error as { cooldown_seconds_remaining?: number })
        ?.cooldown_seconds_remaining;
      if (status === 429 && cooldown) {
        if (onPhoneLoginCooldown) {
          onPhoneLoginCooldown(cooldown);
        }
        setLoginFormStep(LOGIN_FORM_STEP.VERIFY_CODE);
      } else {
        setRequestErrors('Phone number does not have a user associated.');
        console.error(error);
      }
    }
  }, [onPhoneLoginCooldown, setLoginFormStep, username]);

  const buttonLabel = useMemo(() => {
    if (isPhone) {
      return 'Send Code';
    }
    if (isEmail) {
      return 'Login';
    }
    return 'Continue';
  }, [isEmail, isPhone]);

  useEffect(() => {
    // Google Chrome handles autofill differently than other browsers
    // This is a workaround to detect autofill in Chrome
    // and enable the password field and login button
    // This is very much a hack but this is a known issue with Chrome
    // Solution modeled after this article https://medium.com/weekly-webtips/detecting-chrome-autofill-on-page-load-78ea178e4a68
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const detectAutofill = (element: Element): Promise<boolean> => {
      return new Promise((resolve) => {
        timeoutId = setTimeout(() => {
          if (cancelled) {
            return;
          }
          resolve(
            window
              .getComputedStyle(element, null)
              .getPropertyValue('appearance') === 'menulist-button'
          );
        }, 100);
      });
    };

    const testAutofill = async (): Promise<void> => {
      const result = await detectAutofill(inputRef.current);
      if (cancelled) {
        return;
      }
      if (result) {
        setIsEmail(true);
        setIsAutofilled(true);
      }
    };

    if (inputRef.current) {
      void testAutofill();
    }

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        mb: compact ? 0 : 5,
      }}
    >
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        {!extendSession && !compact && (
          <>
            <Logo
              sx={{ marginBottom: '45px', marginLeft: '-5px' }}
              width={200}
            />
            <Typography variant="h4" gutterBottom>
              Login
            </Typography>
          </>
        )}
        {(requestErrors || loginError) && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            data-testid="self-registration-login-error-message"
          >
            {requestErrors}
            {loginError}
          </Alert>
        )}

        <FormikProvider value={formik}>
          <Form autoComplete="on" onSubmit={handleSubmit} role="form">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: compact ? 2.5 : 2,
              }}
            >
              {formStep === LOGIN_FORM_STEP.LOGIN && (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    gap: '10px',
                    alignItems: 'center',
                  }}
                >
                  {extendSession && (
                    <Autocomplete
                      fullWidth
                      onChange={(_, option) => {
                        handleUserFieldChange(option?.value || null);
                      }}
                      sx={{ mt: 1 }}
                      size="medium"
                      value={autocompleteValue}
                      options={userLoginOptions}
                      getOptionLabel={(option: userLoginOption) => option.value}
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Email or Phone Number"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              readOnly: true,
                            },
                          }}
                        />
                      )}
                    />
                  )}
                  {!extendSession && (
                    <TextField
                      sx={{ mt: compact ? 0 : 1 }}
                      label="Enter Email or Phone Number"
                      name="username"
                      type="text"
                      value={username}
                      autoComplete={inputType}
                      inputRef={inputRef}
                      data-testid="self-registration-login-email-input"
                      onPaste={(evt) => {
                        evt.preventDefault();
                        const pasteValue = evt.clipboardData
                          ?.getData('Text')
                          ?.trim();
                        if (!pasteValue) {
                          return;
                        }
                        handleUserFieldChange(pasteValue);
                      }}
                      onChange={(event) =>
                        handleUserFieldChange(event.target.value)
                      }
                      onBlur={(event) => {
                        setIsUsernameFocused(false);
                        handleBlur(event);
                      }}
                      onFocus={() => setIsUsernameFocused(true)}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username && errors.username}
                      fullWidth
                      slotProps={{
                        htmlInput: {
                          maxLength: inputType === INPUT_TYPES.PHONE ? 11 : 300,
                        },

                        inputLabel: {
                          shrink:
                            isAutofilled ||
                            Boolean(username) ||
                            isUsernameFocused,
                        },
                      }}
                    />
                  )}
                </Stack>
              )}
              {showPasswordLogin && (
                <Grow in timeout={prefersReducedMotion ? 0 : 'auto'}>
                  <Stack
                    sx={{
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      mt: compact ? 2 : 1,
                      display: 'flex',
                    }}
                  >
                    <TextField
                      label={'Password'}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      data-testid="self-registration-login-password-input"
                      onChange={handleChange}
                      onBlur={(event) => {
                        setIsPasswordFocused(false);
                        handleBlur(event);
                      }}
                      onFocus={() => setIsPasswordFocused(true)}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      fullWidth
                      slotProps={{
                        input: {
                          autoComplete: 'current-password',
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={toggleShowPassword}
                                edge="end"
                                size="large"
                              >
                                <Icon
                                  icon={showPassword ? eyeFill : eyeOffFill}
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },

                        inputLabel: {
                          shrink:
                            isAutofilled ||
                            Boolean(password) ||
                            isPasswordFocused,
                        },
                      }}
                    />
                    <Button
                      variant={'text'}
                      disabled={isSubmitting}
                      onClick={() =>
                        setLoginFormStep(LOGIN_FORM_STEP.RESET_PASSWORD)
                      }
                    >
                      Forgot your password?
                    </Button>
                  </Stack>
                </Grow>
              )}

              {showCredentialSubmit && (
                <Button
                  fullWidth
                  sx={{ textTransform: 'none' }}
                  type={isEmail ? 'submit' : 'button'}
                  variant="contained"
                  onClick={() => (isPhone ? validatePhone() : handleSubmit())}
                  color="primary"
                  size="large"
                  disabled={(!isEmail && !isPhone) || isSubmitting}
                  loading={isLoading || isSubmitting}
                  data-testid="self-registration-login-submit"
                >
                  {buttonLabel}
                </Button>
              )}

              {promotedProvider !== null ? (
                <Box data-testid="federated-promoted-cta">
                  <ContinueWithProviderButton
                    provider={promotedProvider}
                    email={federatedEmail}
                    variant="primary"
                  />
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      mt: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      or
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={usePasswordFallback}
                      data-testid="federated-use-password-fallback"
                    >
                      use a password
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <>
                  {/* ENG-3395: always offer the IdP. A staff/federated account
                      has no password, so hiding it in the extend-session modal
                      left the dialog with no working action. */}
                  {showGoogleDivider && <Divider sx={{ my: 1 }}>or</Divider>}
                  {baseProviders.map((provider) => (
                    <ContinueWithProviderButton
                      key={provider}
                      provider={provider}
                      email={federatedEmail}
                    />
                  ))}
                </>
              )}

              {/* ENG-3554: announce the promoted primary action for screen
                  readers without moving focus. */}
              <Box
                aria-live="polite"
                data-testid="federated-primary-announcement"
                sx={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  p: 0,
                  m: -1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                {promotedProvider !== null
                  ? `Continue with ${providerLabel(
                      promotedProvider
                    )} is now the primary sign-in option.`
                  : ''}
              </Box>
              {!extendSession && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                >
                  By continuing, you agree to the{' '}
                  <a
                    href="https://www.prontohousing.com/terms-of-service"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.prontohousing.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </Typography>
              )}

              {!extendSession && !compact && (
                <Button
                  variant="text"
                  onClick={() => navigate('/account-recovery')}
                  sx={{ mt: 1 }}
                >
                  Can&apos;t sign in?
                </Button>
              )}
            </Box>
          </Form>
        </FormikProvider>
      </Box>
    </Stack>
  );
};

export default LoginFormV2;
